/**
 * @description Hook that manages Tokenex SDK integration:
 * - Loads the Tokenex SDK script
 * - Handles initialization
 * - Generates configuration objects for card and CVV fields
 *
 * @param {AnyGatewayEnv} [env='prod'] - Environment to use for the gateway
 * @param {string} [apiToken=''] - Token used for CB payment service authentication
 * @param {AnyGatewayMode} [mode='live'] - Mode for the Tokenex SDK (live/test)
 * @returns {GatewayHookResponse} Hook response containing loading state and configuration
 */

import {
  TokenexFormConfigType,
  TokenexModeType,
  TokenexSchemaConfigType,
  UseGatewayParams,
  AnyGatewayEnv,
  AnyGatewayMode,
} from '../@types';
import { ApiHosts, TokenexScriptUrl } from '../constants';
import { createLogLocal } from '../utils/createLogLocal';

const log = createLogLocal('useTokenex.ts');

/**
 * @description Loads the Tokenex SDK script into the document.
 * @param {TokenexModeType} mode - The Tokenex mode ('live' or 'test').
 * @param {boolean} reload - Whether to force reload the script.
 * @returns {Promise<void>} Resolves when the script is loaded.
 */
const loadTokenexSDK = (mode: TokenexModeType = 'live', reload = false) =>
  new Promise<void>(resolve => {
    if (!reload && (window as any).TokenEx) {
      log('Tokenex already Loaded.');
      resolve();
    }

    const script = document.createElement('script');
    script.src = TokenexScriptUrl[mode] || TokenexScriptUrl.live;
    script.async = true;

    script.addEventListener('load', async () => {
      log('Tokenex loaded.');
      resolve();
    });

    document.body.appendChild(script);
  });

type FetchTokenexParams = {
  env: AnyGatewayEnv;
  apiToken: string;
  mode: AnyGatewayMode;
  propertyId?: string;
};

/**
 * @description Fetches the Tokenex configuration from the backend for the given environment and mode.
 * @param {FetchTokenexParams} params - Parameters for fetching Tokenex config.
 * @returns {Promise<TokenexFormConfigType['options'] | null>} The Tokenex config options or null.
 */
const fetchTokenexConfig = async ({
  env = 'prod',
  apiToken = '',
  mode = 'live',
  propertyId,
}: FetchTokenexParams) => {
  window.console.log('Fetching Tokenex config...');
  const apiHost = ApiHosts[env].host;
  const authKeyUrl = `${apiHost}/payments-configuration/v1.0/tokenex/authentication_key`;
  let response = {} as Response;

  try {
    response = await fetch(authKeyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(apiToken && { Authorization: `Bearer ${apiToken}` }),
        // Authorization: `Bearer ${apiToken}`,
        Origin: window.location.origin,
        // Origin: window.location.origin,
      },
      body: JSON.stringify({
        origins: [window.location.origin],
        // origins: [window.location.origin],
        mode: mode.toUpperCase(),
      }),
    });
  } catch (_error) {
    window.console.log('Can not load Tokenex config:', _error);
  }

  window.console.log(
    'Fetched Tokenex config from primary endpoint.',
    response.ok,
  );
  // TODO: fix 'any' type later
  let data = {} as any;

  if (response.ok) {
    data = await response.json();
  } else {
    // throw new Error('Network response was not ok');
    // TEMPORARY: Attempt to fetch from pbl settings endpoint
    window.console.log(
      `Failed to fetch Tokenex config from ${authKeyUrl}, status: ${response.status}. Refetching from PBL settings endpoint...`,
    );
    try {
      response = await fetch(`${apiHost}/payment/PaymentElement/v2/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(apiToken && { Authorization: `Bearer ${apiToken}` }),
          // Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({
          property_id: propertyId,
          origin: window.location.origin,
          payment_method_filters: ['card'],
        }),
      });
    } catch (_error) {
      window.console.log('Can not load Tokenex config:', _error);
      return null;
    }
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const parsed = await response.json();
    const tokenexData =
      parsed?.data?.payment_method_configurations?.card?.tokenization
        ?.tokenex || null;

    window.console.log(
      'Refetched Tokenex config from PBL settings endpoint.',
      response.ok,
      parsed,
      tokenexData,
    );

    if (!tokenexData) {
      throw new Error('Network response was not ok');
    }

    data = {
      authentication_key: [
        {
          authentication_key: tokenexData.cards_authentication_key,
          scheme: tokenexData.cards_schema,
        },
        {
          authentication_key: tokenexData.cvv_authentication_key,
          scheme: tokenexData.cvv_schema,
        },
      ],
      client_id: +tokenexData.tokenex_id,
      timestamp: tokenexData.timestamp,
    };
  }

  const {
    authentication_key: authKeys,
    client_id: tokenExID,
    timestamp,
  } = data;

  const tokenexConfig = (
    authKeys as Record<'authentication_key' | 'scheme', string>[]
  ).reduce<TokenexSchemaConfigType>((a, b) => {
    return {
      ...a,
      ...(b.scheme.includes('GUID') && {
        cvvAuthKey: b.authentication_key,
        cvvScheme: b.scheme,
      }),
      ...(!b.scheme.includes('GUID') && {
        cardAuthKey: b.authentication_key,
        cardScheme: b.scheme,
      }),
    };
  }, {} as TokenexSchemaConfigType);

  window.console.log('tokenexConfig:', tokenexConfig);
  return {
    card: {
      origin: window.location.origin,
      timestamp,
      tokenExID,
      tokenScheme: tokenexConfig.cardScheme,
      authenticationKey: tokenexConfig.cardAuthKey,
    },
    cvv: {
      origin: window.location.origin,
      timestamp,
      tokenExID,
      tokenScheme: tokenexConfig.cvvScheme,
      authenticationKey: tokenexConfig.cvvAuthKey,
    },
  } as TokenexFormConfigType['options'];
};

/**
 * @description Loads the Tokenex configuration and SDK, returning the provider and options.
 * @param {Omit<UseGatewayParams, 'propertyId'>} params - Parameters for loading Tokenex.
 * @returns {Promise<TokenexFormConfigType | null>} The Tokenex form config or null.
 */
export const loadTokenex = async ({
  mode,
  env,
  apiToken,
  propertyId,
}: UseGatewayParams) => {
  return fetchTokenexConfig({
    mode,
    env,
    apiToken,
    propertyId,
  })
    .then(data => {
      if (!data) {
        return null;
      }
      return loadTokenexSDK(mode)
        .then(() => {
          return {
            provider: 'tokenex',
            options: data,
          } as TokenexFormConfigType;
        })
        .catch(() => {
          return null;
        });
    })
    .catch(() => {
      return null;
    });
};
